import type { FC } from "react";

import { systemTaskType } from "@recobra/schemas/task-catalog";
import { FieldGroup } from "@recobra/ui/field";

import { submitForm } from "../../lib/form";
import { useTaskTypes } from "../task-data";
import { TaskContextSummary } from "../task-display";
import { TaskSummaryColumns } from "../task-display";
import { DvcSearchSection } from "./DvcSearchSection";
import { RequestedDocumentsSection } from "./RequestedDocumentsSection";
import { collectsDvcSearchDetails, collectsRequestedDocuments } from "./TaskFormValues";
import { TaskObservationSection } from "./TaskObservationSection";
import { TaskSchedulingSection } from "./TaskSchedulingSection";
import { TaskTypeSection } from "./TaskTypeSection";
import type { TaskCreationFormApi, TaskFormContext } from "./useTaskCreationForm";

export const TaskCreationForm: FC<{
  form: TaskCreationFormApi;
  formId: string;
  isOpen: boolean;
  taskTypes?: readonly string[];
  context: TaskFormContext;
}> = ({ form, formId, isOpen, taskTypes, context }) => (
  <form id={formId} onSubmit={submitForm(form)}>
    <form.AppForm>
      <FieldGroup>
        <TaskCreationFields form={form} isOpen={isOpen} taskTypes={taskTypes} context={context} />
      </FieldGroup>
    </form.AppForm>
  </form>
);

const TaskCreationFields: FC<{
  form: TaskCreationFormApi;
  isOpen: boolean;
  taskTypes?: readonly string[];
  context: TaskFormContext;
}> = ({ form, isOpen, taskTypes, context: { contextType, contextId } }) => {
  const types = useTaskTypes();
  return (
    <>
      <TaskTypeSection
        form={form}
        taskTypes={taskTypes}
        contextType={contextType}
        contextId={contextId}
      />

      <TaskSummaryColumns>
        <TaskContextSummary isOpen={isOpen} contextType={contextType} contextId={contextId} />
        <TaskSchedulingSection
          form={form}
          isOpen={isOpen}
          contextType={contextType}
          contextId={contextId}
        />
      </TaskSummaryColumns>

      <form.Subscribe selector={(state) => state.values.taskType}>
        {(taskType) => {
          const system = types.data?.get(taskType)?.system ?? null;
          return (
            <>
              {collectsDvcSearchDetails(system) ? (
                <DvcSearchSection form={form} contextType={contextType} contextId={contextId} />
              ) : null}
              {collectsRequestedDocuments(system) ? (
                <RequestedDocumentsSection
                  form={form}
                  contextType={contextType}
                  contextId={contextId}
                />
              ) : null}
              {systemTaskType(system)?.collects?.observation ? (
                <TaskObservationSection form={form} />
              ) : null}
            </>
          );
        }}
      </form.Subscribe>
    </>
  );
};
